import {
  faDiamondExclamation,
  faXmark,
} from "@fortawesome/pro-solid-svg-icons";
import { Box, Button, ButtonProps, Stack, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useState } from "react";
import { useMutation } from "urql";

import { DeleteInference } from "@/components/inference/queries.graphql";
import { Icon } from "@/components/ui-v2";

interface DismissButtonProps extends ButtonProps {
  inferenceId?: string;
}

const Root = styled("div")(() => ({
  width: "100%",
  aspectRatio: "1/1",
  backgroundColor: "transparent",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const CircleContainer = styled(Box)(({ theme }) => ({
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  padding: theme.spacing(2),
  width: "80%",
  height: "80%",
  maxWidth: "500px",
  maxHeight: "500px",
  borderRadius: "50%",
  border: `1px solid ${theme.palette.error.main}`,
}));

const StyledErrorMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  textAlign: "center",
}));

interface FileImageErroredProps extends React.HTMLAttributes<HTMLDivElement> {
  inferenceId?: string;
  dismissable?: boolean;
  errorMessage?: string;
  dismissButtonProps?: DismissButtonProps;
}

const DismissButton = (props: DismissButtonProps) => {
  const { inferenceId, ...rest } = props;
  const [isHovered, setIsHovered] = useState(false);
  const [, deleteInference] = useMutation(DeleteInference);

  const handleDismiss = () => {
    if (!inferenceId) return;
    deleteInference({ input: { inferenceId } });
  };

  return (
    <Button
      variant="text"
      size="small"
      color="error"
      disabled={!inferenceId}
      onClick={handleDismiss}
      startIcon={
        isHovered ? (
          <Icon icon={faXmark} />
        ) : (
          <Icon icon={faDiamondExclamation} />
        )
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...rest}
    >
      {isHovered ? "Dismiss" : "Error"}
    </Button>
  );
};

export const FileImageErrored = (props: FileImageErroredProps) => {
  const {
    inferenceId,
    errorMessage = "",
    dismissable = true,
    dismissButtonProps = {},
    ...rest
  } = props;

  return (
    <Root {...rest}>
      <CircleContainer>
        <Stack
          spacing={1}
          sx={{
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {dismissable && (
            <DismissButton
              inferenceId={inferenceId}
              variant={errorMessage ? "contained" : "text"}
              {...dismissButtonProps}
            />
          )}
          {errorMessage && (
            <StyledErrorMessage variant="caption" display="block">
              {errorMessage}
            </StyledErrorMessage>
          )}
        </Stack>
      </CircleContainer>
    </Root>
  );
};
